<script setup>
import { computed, onMounted } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsLayout from 'dashboard/components-next/Settings/SettingsLayout.vue';
import IntegrationEmptyState from '../shared/IntegrationEmptyState.vue';
import IntegrationConnectionCard from '../shared/IntegrationConnectionCard.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const uiFlags = computed(() => getters['hubspots/getUIFlags'].value || {});
const hubspots = computed(() => getters['hubspots/getHubspots'].value || []);

const isLoading = computed(
  () => uiFlags.value.isFetching || uiFlags.value.isCreating
);
const isEmpty = computed(() => hubspots.value.length === 0);
const isConnected = computed(() => hubspots.value.length > 0);
const hubspot = computed(() => hubspots.value[0] || null);

onMounted(() => {
  store.dispatch('hubspots/get');
});

const handleConnect = async () => {
  try {
    await store.dispatch('hubspots/authorize');
  } catch (error) {
    useAlert(error?.message || t('INTEGRATION_SETTINGS.HUBSPOT.CONNECT_ERROR'));
  }
};

const handleDisconnect = async () => {
  // eslint-disable-next-line no-alert
  if (window.confirm(t('INTEGRATION_SETTINGS.HUBSPOT.DISCONNECT.CONFIRM'))) {
    try {
      await store.dispatch('hubspots/delete', hubspot.value.id);
      useAlert(t('INTEGRATION_SETTINGS.HUBSPOT.DISCONNECT.SUCCESS'));
    } catch (error) {
      useAlert(
        error?.message || t('INTEGRATION_SETTINGS.HUBSPOT.DISCONNECT.ERROR')
      );
    }
  }
};
</script>

<template>
  <SettingsLayout
    :header-title="$t('INTEGRATION_SETTINGS.HUBSPOT.HEADER')"
    :button-label="
      isConnected
        ? $t('INTEGRATION_SETTINGS.HUBSPOT.DISCONNECT_BUTTON')
        : $t('INTEGRATION_SETTINGS.HUBSPOT.CONNECT_BUTTON')
    "
    @click="isConnected ? handleDisconnect() : handleConnect()"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <IntegrationEmptyState
      v-else-if="isEmpty"
      icon="i-lucide-link"
      :title="$t('INTEGRATION_SETTINGS.HUBSPOT.EMPTY_STATE')"
      :description="$t('INTEGRATION_SETTINGS.HUBSPOT.DESCRIPTION')"
      :button-label="$t('INTEGRATION_SETTINGS.HUBSPOT.CONNECT_BUTTON')"
      @action="handleConnect"
    />

    <div v-else class="space-y-4">
      <IntegrationConnectionCard
        icon="i-lucide-link"
        icon-class="bg-gradient-to-br from-orange-500 to-orange-600"
        :title="
          hubspot?.account_name ||
          hubspot?.settings?.account_name ||
          $t('INTEGRATION_SETTINGS.HUBSPOT.CONNECTED')
        "
        :description="$t('INTEGRATION_SETTINGS.HUBSPOT.CONNECTED_DESCRIPTION')"
      />
    </div>
  </SettingsLayout>
</template>
